<template>
    <div v-if="visible" class="update-splash-debug">
        <img class="update-splash-logo" alt="Sportspoule logo" :src="logoSrc" />
        <div v-if="!logoOnly" class="update-splash-panel">
            <p class="current-step">{{ step }}</p>
            <div class="progress-track">
                <div
                    class="progress-fill"
                    :style="{ width: `${normalizedProgress}%` }"
                ></div>
            </div>
        </div>
        <div v-if="!logoOnly && debugLines.length" class="debug-panel">
            <p v-for="(line, i) in debugLines" :key="i" class="debug-line">
                {{ line }}
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import logoSrc from "@/assets/images/subtract.svg?url";

    const props = defineProps<{
        visible: boolean;
        progress: number;
        step: string;
        debugLines: string[];
        logoOnly?: boolean;
    }>();

    const normalizedProgress = computed(() => {
        const value = Number.isFinite(props.progress) ? props.progress : 0;
        return Math.min(100, Math.max(0, Math.round(value)));
    });
</script>

<style scoped lang="scss">
    .update-splash-debug {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 24px;
        background: radial-gradient(circle at center, #1f5dff 0%, #0c34c5 100%);
        padding: 32px;
    }

    .update-splash-logo {
        width: 132px;
        height: auto;
        animation:
            splash-logo-pulse 1.6s ease-in-out infinite,
            splash-logo-drift 3.2s ease-in-out infinite;
        transform-origin: center;
        filter: drop-shadow(0 4px 16px rgba(255, 255, 255, 0.3));
    }

    .update-splash-panel {
        width: min(420px, 92vw);
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 14px;
        padding: 12px;
        backdrop-filter: blur(8px);
    }

    .current-step {
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        margin: 0 0 10px;
        text-align: center;
    }

    .progress-track {
        width: 100%;
        height: 8px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.25);
        overflow: hidden;
    }

    .progress-fill {
        height: 100%;
        background: #fff;
        border-radius: 999px;
        transition: width 0.4s ease;
    }

    @keyframes splash-logo-pulse {
        0%,
        100% {
            opacity: 0.8;
            transform: scale(0.96);
        }

        50% {
            opacity: 1;
            transform: scale(1.04);
        }
    }

    @keyframes splash-logo-drift {
        0%,
        100% {
            translate: 0 0;
        }

        50% {
            translate: 0 -4px;
        }
    }

    .debug-panel {
        width: min(420px, 92vw);
        max-height: 30vh;
        overflow-y: auto;
        background: rgba(0, 0, 0, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 10px;
        padding: 8px 10px;
        margin-top: 4px;
    }

    .debug-line {
        color: rgba(255, 255, 255, 0.85);
        font-size: 10px;
        font-family: monospace;
        margin: 2px 0;
        line-height: 1.3;
        word-break: break-all;
    }
</style>
